import { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { MentionItem } from './useAtMention';
import { tosThumbnail } from '../../../utils/tosImage';
import { tosAccelerate } from '../../../utils/tosAccelerate';

interface AssetMentionMenuProps {
  items: MentionItem[];
  selectedId: string | null;
  // 视口坐标系的最终落点（已由接线层叠加 textarea 视口偏移/滚动并决定下/上翻转）。
  // 菜单 portal 到 body 且 position:fixed，故 coords 为视口坐标，规避祖先 overflow:hidden 裁剪。
  coords: { top: number; left: number };
  onPick: (id: string) => void;
  onHover: (id: string) => void;
}

const thumbStyle: CSSProperties = {
  width: '28px',
  height: '28px',
  objectFit: 'cover',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  flexShrink: 0,
};

export default function AssetMentionMenu({ items, selectedId, coords, onPick, onHover }: AssetMentionMenuProps) {
  return createPortal(
    <div
      role="listbox"
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        zIndex: 1000,
        maxHeight: '220px',
        overflowY: 'auto',
        minWidth: '180px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
        padding: '4px',
      }}
    >
      {items.map((item) => {
        const selected = item.id === selectedId;
        return (
          <div
            key={item.id}
            role="option"
            aria-selected={selected}
            data-mention-id={item.id}
            // onMouseDown + preventDefault：在 textarea blur 之前完成选择，不丢点击。
            onMouseDown={(e) => { e.preventDefault(); onPick(item.id); }}
            onMouseEnter={() => onHover(item.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px 6px',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              background: selected ? 'var(--surface-2)' : 'transparent',
              outline: selected ? '2px solid var(--theme-storyboard)' : 'none',
              outlineOffset: '-2px',
            }}
          >
            {item.kind === 'image' && item.thumbnailUrl ? (
              <img src={tosThumbnail(item.thumbnailUrl, 56)} alt={item.label} style={thumbStyle} />
            ) : item.kind === 'video' && item.thumbnailUrl ? (
              <video src={tosAccelerate(item.thumbnailUrl)} preload="metadata" style={thumbStyle} />
            ) : (
              <div style={{ ...thumbStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)' }}>
                {item.kind === 'audio' ? '🔊' : '⚠'}
              </div>
            )}
            <span style={{ fontWeight: 600, color: 'var(--theme-storyboard)', fontSize: '0.8rem' }}>{item.token}</span>
            <span style={{ color: 'var(--text-2)', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
          </div>
        );
      })}
    </div>,
    document.body
  );
}
